import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { PageLoader } from "@/components/common";
import { BookOpen, Crown, FileText, Target, Bookmark, PlayCircle, ArrowRight, Flame, CalendarDays, Clock, Newspaper, ClipboardList, Zap, ChevronRight, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["student-dash"], queryFn: () => get("/dashboard/student") });
  if (isLoading || !data) return <PageLoader />;
  const s = data.stats;
  const trend = data.performance_trend || [];
  const startTest = async (tid) => {
    try { const r = await post(`/tests/${tid}/start`); nav(`/exam/${r.attempt_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };
  const resume = () => { const c = data.continue_learning[0]; nav(c ? `/app/courses/${c.course.id}` : "/app/courses"); };

  return (
    <div className="space-y-6">
      <Hero name={data.profile_name} daily={data.daily_quiz} onQuiz={() => startTest(data.daily_quiz.id)} onResume={resume} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={BookOpen} label="Active Courses" value={s.active_courses} tone="blue" testid="stat-courses" />
        <Stat icon={Crown} label="Subscriptions" value={s.active_subscriptions} tone="green" testid="stat-subs" />
        <Stat icon={FileText} label="Tests Taken" value={s.tests_attempted} tone="violet" testid="stat-tests" series={trend.map((t) => t.score)} />
        <Stat icon={Target} label="Avg. Accuracy" value={`${s.avg_accuracy}%`} tone="amber" testid="stat-accuracy" series={trend.map((t) => t.accuracy)} />
        <Stat icon={Bookmark} label="Saved Questions" value={s.bookmarks} tone="rose" testid="stat-saved" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ContinueLearning data={data} nav={nav} />
          <RecentPerformance attempts={data.recent_attempts} nav={nav} />
        </div>
        <UpcomingTests tests={data.upcoming_tests} daily={data.daily_quiz} onStart={startTest} nav={nav} />
      </div>
    </div>
  );
}

function Hero({ name, daily, onQuiz, onResume }) {
  return (
    <div className="rt-gradient relative overflow-hidden rounded-3xl text-white" data-testid="dash-hero">
      <div className="absolute -right-10 -top-20 h-72 w-72 rounded-full bg-white/10" />
      <div className="absolute right-60 -bottom-24 h-56 w-56 rounded-full bg-white/5" />
      <div className="relative grid md:grid-cols-[1fr_auto] items-center">
        <div className="p-6 sm:p-9">
          <p className="text-indigo-100 text-lg">Welcome back,</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-1" data-testid="dash-student-name">{name} <span className="inline-block origin-bottom-right animate-[wave_2s_ease-in-out_infinite]">👋</span></h1>
          <p className="text-indigo-100 mt-3 text-sm sm:text-base max-w-lg">Keep the streak alive — your next rank is one test away. Learn. Practice. Analyze. Improve.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            {daily && <button onClick={onQuiz} data-testid="daily-quiz-cta" className="bg-white text-slate-900 font-bold px-5 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:-translate-y-0.5 transition-transform"><Flame className="h-5 w-5 text-orange-500" /> Take Daily Quiz</button>}
            <button onClick={onResume} data-testid="resume-learning-btn" className="border border-white/60 text-white font-semibold px-5 py-3 rounded-xl flex items-center gap-2 hover:bg-white/10 transition-colors"><PlayCircle className="h-5 w-5" /> Resume Learning</button>
          </div>
        </div>
        <div className="hidden md:block pr-6 py-5">
          <img src="/illustrations/hero.jpg" alt="Student studying" className="st-float h-56 w-[340px] object-cover rounded-3xl ring-4 ring-white/20 shadow-2xl" />
        </div>
      </div>
    </div>
  );
}

const TONES = {
  blue: ["bg-blue-50 text-blue-600", "#3B82F6"], green: ["bg-emerald-50 text-emerald-600", "#10B981"],
  violet: ["bg-violet-50 text-violet-600", "#8B5CF6"], amber: ["bg-amber-50 text-amber-600", "#F59E0B"], rose: ["bg-rose-50 text-rose-600", "#F43F5E"],
};

function Sparkline({ series, color }) {
  if (!series || series.length < 2) return null;
  const max = Math.max(...series), min = Math.min(...series), r = max - min || 1;
  const pts = series.map((v, i) => `${(i / (series.length - 1)) * 100},${28 - ((v - min) / r) * 24}`).join(" ");
  return <svg viewBox="0 0 100 30" className="w-16 xl:w-20 h-7" preserveAspectRatio="none"><polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" /></svg>;
}

function Stat({ icon: Icon, label, value, tone, testid, series }) {
  const [cls, color] = TONES[tone];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 card-hover flex items-center gap-3" data-testid={testid}>
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${cls}`}><Icon className="h-6 w-6" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 leading-tight">{label}</p>
        <div className="flex items-end justify-between gap-2">
          <p className="text-2xl font-display font-extrabold text-slate-900">{value}</p>
          <div className="hidden sm:block"><Sparkline series={series} color={color} /></div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, sub, action, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div><h2 className="font-display font-bold text-xl text-slate-900">{title}</h2>{sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

const LinkBtn = ({ label, onClick, testid }) => <button onClick={onClick} data-testid={testid} className="text-sm text-indigo-600 font-semibold flex items-center gap-1 whitespace-nowrap hover:gap-2 transition-all">{label} <ArrowRight className="h-4 w-4" /></button>;

function ContinueLearning({ data, nav }) {
  const recs = [
    { icon: ClipboardList, tone: "bg-emerald-50 text-emerald-600", t: "Previous Papers", s: "Real exam papers", to: "/app/pyp" },
    { icon: Newspaper, tone: "bg-orange-50 text-orange-600", t: "Current Affairs", s: "Daily updates", to: "/app/current-affairs" },
    { icon: FileText, tone: "bg-violet-50 text-violet-600", t: "Test Series", s: "Full & sectional mocks", to: "/app/tests" },
  ];
  const courseRecs = (data.recommended || []).slice(0, 3).map((c) => ({ icon: BookOpen, tone: "bg-blue-50 text-blue-600", t: c.title, s: c.category || "Course", to: `/app/courses/${c.id}` }));
  const tiles = courseRecs.length ? courseRecs : recs;
  return (
    <Card title="Continue Learning" sub="Pick up where you left off and keep moving forward." testid="continue-learning"
      action={<LinkBtn label="View All Courses" onClick={() => nav("/app/courses")} testid="view-all-courses" />}>
      {data.continue_learning.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-5 text-center text-sm text-slate-500">No lessons in progress yet. <button onClick={() => nav("/app/courses")} className="text-indigo-600 font-semibold">Explore courses</button></div>
      ) : data.continue_learning.slice(0, 2).map((c) => (
        <div key={c.lesson_id} className="flex flex-wrap items-center gap-4 p-4 rounded-2xl border border-slate-200 mb-3" data-testid={`continue-${c.lesson_id}`}>
          <div className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><BookOpen className="h-7 w-7" /></div>
          <div className="flex-1 min-w-[180px]">
            <p className="font-bold text-slate-900">{c.course.title}</p>
            <p className="text-xs text-slate-500">Last watched: {c.lesson?.title}</p>
            <div className="flex items-center gap-3 mt-2"><div className="h-2 flex-1 bg-slate-100 rounded-full"><div className="h-2 bg-indigo-600 rounded-full" style={{ width: `${c.percent}%` }} /></div><span className="text-xs font-bold text-slate-700">{c.percent}%</span></div>
          </div>
          <button onClick={() => nav(`/app/courses/${c.course.id}`)} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2"><PlayCircle className="h-4 w-4" /> Continue</button>
        </div>
      ))}
      <h3 className="font-display font-bold text-slate-900 mt-5 mb-3">Recommended for You</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        {tiles.map((r) => (
          <button key={r.t} onClick={() => nav(r.to)} data-testid={`rec-${r.t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 text-left card-hover">
            <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${r.tone}`}><r.icon className="h-5 w-5" /></span>
            <span className="flex-1 min-w-0"><span className="block font-semibold text-slate-800 text-sm truncate">{r.t}</span><span className="block text-xs text-slate-500 truncate">{r.s}</span></span>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        ))}
      </div>
    </Card>
  );
}

function RecentPerformance({ attempts, nav }) {
  return (
    <Card title="Recent Performance" testid="recent-performance" action={<LinkBtn label="View Performance" onClick={() => nav("/app/performance")} testid="view-performance" />}>
      {attempts.length === 0 ? <p className="text-sm text-slate-500">No attempts yet — take your first mock test.</p>
        : <div className="divide-y divide-slate-100">{attempts.map((a) => (
          <button key={a.id} onClick={() => nav(`/results/${a.id}`)} className="w-full py-3 flex items-center gap-3 text-left" data-testid={`recent-attempt-${a.id}`}>
            <span className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><TrendingUp className="h-4 w-4" /></span>
            <span className="flex-1 min-w-0"><span className="block font-medium text-slate-800 truncate">{a.test_title}</span><span className="block text-xs text-slate-500">Accuracy {a.accuracy}%</span></span>
            <span className="font-display font-bold text-indigo-700">{a.score}/{a.max_score}</span>
          </button>))}</div>}
    </Card>
  );
}

function UpcomingTests({ tests, daily, onStart, nav }) {
  return (
    <Card title="Upcoming Tests" testid="upcoming-tests" action={<LinkBtn label="View All" onClick={() => nav("/app/tests")} testid="view-all-tests" />}>
      <div className="space-y-3">
        {tests.length === 0 && <p className="text-sm text-slate-500">No tests available right now.</p>}
        {tests.map((t) => (
          <div key={t.id} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200" data-testid={`upcoming-test-${t.id}`}>
            <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${t.test_type === "daily_quiz" ? "bg-orange-50 text-orange-500" : "bg-indigo-50 text-indigo-600"}`}>{t.test_type === "daily_quiz" ? <Flame className="h-5 w-5" /> : <FileText className="h-5 w-5" />}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm truncate">{t.title}</p>
              <p className="text-xs text-slate-500 flex items-center gap-3 mt-0.5"><span className="flex items-center gap-1"><Clock className="h-3 w-3" />{t.duration_min} min</span><span>{t.total_questions} Qs</span>{t.is_free ? <span className="text-emerald-600 font-semibold">Free</span> : <span className="text-amber-600 font-semibold">₹{t.price}</span>}</p>
            </div>
            <button onClick={() => onStart(t.id)} data-testid={`dash-start-test-${t.id}`} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg">Start</button>
          </div>
        ))}
        <button onClick={() => (daily ? onStart(daily.id) : nav("/app/daily-quiz"))} data-testid="daily-quiz-promo" className="w-full flex items-center gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-100 text-left hover:bg-rose-100/70 transition-colors">
          <span className="h-11 w-11 rounded-xl bg-white text-rose-500 flex items-center justify-center shrink-0"><CalendarDays className="h-5 w-5" /></span>
          <span className="flex-1"><span className="block font-bold text-rose-600 text-sm">Stay Ahead with Daily Quiz</span><span className="block text-xs text-slate-500">A fresh quiz every day to keep your streak alive.</span></span>
          <Zap className="h-4 w-4 text-rose-500" />
        </button>
      </div>
    </Card>
  );
}
